"use client";

import { skipToken, useQuery } from "@tanstack/react-query";
import {
	Outlet,
	useLocation,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";

import { authClient } from "@/auth";
import { InfoLabel } from "@/components/catalyst/Fieldset";
import { Navbar, NavbarSpacer } from "@/components/catalyst/Navbar";
import { SidebarLayout } from "@/components/catalyst/SidebarLayout";
import { Switch, SwitchField } from "@/components/catalyst/Switch";
import { useZodServerError } from "@/hooks/use-zod-server-error";
import { useFeatureFlagEnabled } from "@/integrations/posthog/feature-flags";
import { orpc } from "@/orpc/client";
import { useAutosaveSection } from "@/pages/app-detail/hooks/use-auto-save";
import { SKIP } from "@/util/skip-token";
import { AppSidebar } from "./components/AppSidebar";
import { Breadcrumbs } from "./components/Breadcrumbs";
import { EmailVerificationBanner } from "./components/EmailVerificationBanner";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { NotificationDropdown } from "./components/NotificationDropdown";
import { PageTitle } from "./components/PageTitle";
import { ProfileModal } from "./components/ProfileModal";
import { SubscriptionStatusBanner } from "./components/SubscriptionStatusBanner";
import { navigation as staticNavigation } from "./constants";

export function MainLayout() {
	const navigate = useNavigate();
	const location = useLocation();
	const { setZodServerError } = useZodServerError();
	const [isProfileOpen, setIsProfileOpen] = useState(false);
	const testModeFeatureFlag = useFeatureFlagEnabled("testMode");

	const params = useParams({
		from: "/_app/apps/$appId/",
		shouldThrow: false,
	});

	const { data: app } = useQuery(
		orpc.apps.detail.queryOptions({
			input: params?.appId ? { id: params.appId } : skipToken,
		}),
	);

	const handleSignOut = useCallback(() => {
		authClient.signOut({
			fetchOptions: {
				onSuccess() {
					navigate({ to: "/auth/signin" });
				},
			},
		});
	}, [navigate]);

	const testMode = useAutosaveSection<boolean>({
		initialValue: app?.testMode ?? false,
		buildPayload: (value) =>
			app?.id ? { id: app?.id, testMode: value } : SKIP,
		onError: setZodServerError,
	});

	const navigation = useMemo(() => {
		return staticNavigation.map((item) => ({
			name: item.name,
			href: item.href,
			icon: item.icon,
			current:
				location.pathname === item.href ||
				(item.href !== "/dashboard" && location.pathname.startsWith(item.href)),
		}));
	}, [location.pathname]);

	const userNavigation = useMemo(
		() => [
			{ name: "Your profile", onClick: () => setIsProfileOpen(true) },
			{ name: "Sign out", onClick: handleSignOut },
		],
		[handleSignOut],
	);

	return (
		<SidebarLayout
			navbar={
				<Navbar>
					<NavbarSpacer />
					{/* Add navbar items here if needed for mobile specific actions */}
				</Navbar>
			}
			sidebar={
				<AppSidebar navigation={navigation} userNavigation={userNavigation} />
			}
		>
			<ImpersonationBanner />
			<EmailVerificationBanner />
			<SubscriptionStatusBanner />
			<div className="flex items-center justify-between gap-4 pb-4 mb-4 border-b border-base-content/5">
				<Breadcrumbs />
				<div className="flex items-center gap-8">
					{testModeFeatureFlag && app && (
						<SwitchField className="gap-4!">
							<InfoLabel
								htmlFor="test-mode"
								className="text-xs font-semibold"
								info="Enable test mode to simulate flows and test your app."
							>
								Test Mode
							</InfoLabel>
							<Switch
								color="secondary"
								id="test-mode"
								checked={testMode.value}
								onChange={testMode.update}
							/>
						</SwitchField>
					)}

					<NotificationDropdown />
				</div>
			</div>

			<div className="mb-6">
				<PageTitle />
			</div>
			<Outlet />
			<ProfileModal
				isOpen={isProfileOpen}
				onClose={() => setIsProfileOpen(false)}
			/>
		</SidebarLayout>
	);
}
